import type { Middleware } from 'openapi-fetch'
import createClient from 'openapi-fetch'
import { AppConfigKeys } from './enums'
import type { paths } from './paapi'

export * from './changelog'
export * from './claimlab'
export * from './enums'
export * from './exclusion'
export * from './featureFlagsRpc'
export * from './paapi-endpoints'
export * from './policy'
export * from './validation'
export * from './workers-api'

import type { FilterItem } from './paapi-endpoints'

export type FilterChangePayload = {
  item: FilterItem & {
    active?: boolean
    disabled?: boolean
    selected?: boolean
    group?: string
    isUserAdded?: boolean
  }
  name: string
}

export type PaAPIClientMiddleware = Middleware
export type DropDownListItem = {
  id: string
  active?: boolean
  label: string
  display_name?: string
  description?: string
  disabled?: boolean
}

export const WORKSPACE_ID_HEADER = 'x-rialtic-workspace-id'

export declare type HeadersArg =
  | Headers
  | Partial<Record<string, string>>
  | (Record<string, string> & {
      Authorization: string
      [WORKSPACE_ID_HEADER]?: string
    })

const forwardedHeaders: Record<string, boolean> = {
  accept: true,
  authorization: true,
  [WORKSPACE_ID_HEADER]: true,
  'cache-control': true,
  'content-type': true,
  cookie: true,
  'if-none-match': true,
  'if-modified-since': true,
  origin: true,
  traceparent: true,
}

const mergeAndFilterHeaders = (req: Request, headers: HeadersArg): Request => {
  if (headers instanceof Headers) {
    headers.forEach((value, key) => {
      if (forwardedHeaders[key.toLowerCase()]) req.headers.set(key, value)
    })

    return req
  }

  for (const [key, value] of Object.entries(headers)) {
    if (forwardedHeaders[key.toLowerCase()]) req.headers.set(key, value ?? '')
  }

  return req
}

export const createPaAPIClient = (baseUrl: string, headers: HeadersArg) => {
  const middleware: Middleware = {
    async onRequest(req) {
      return mergeAndFilterHeaders(req, headers)
    },
  }
  const client = createClient<paths>({ baseUrl })

  client.use(middleware)

  return client
}

export type KvKeys = {
  name: string
}

export const parseAppConfig = (keys: KvKeys[]) => {
  return keys.reduce(
    (state, { name }) => {
      state[name.replace('app:config:', '') as AppConfigKeys] = true
      return state
    },
    {
      [AppConfigKeys.maintenance]: false,
    } as Record<AppConfigKeys, boolean>,
  )
}

export const featureFlags = {
  [AppConfigKeys.featureClaimIDRedirect]: false,
  [AppConfigKeys.featurePeriodicRefresh]: false,
  [AppConfigKeys.featureClaimChain]: false,
  [AppConfigKeys.featureExclusionClaimLabV2]: false,
  [AppConfigKeys.maintenance]: false,
} as const

type UnleashStrategy = {
  name: string
  // parameters: Object //TODO: needs more detail here or get types from a lib, irrelevant for Gitlab's version
}

export type UnleashFeatureFlag = {
  description: string
  enabled: boolean
  name: string
  strategies: UnleashStrategy[]
  type: 'configuration' | 'feature'
  value: string | boolean | number | Date
}
